import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import { ADMIN_ROLES } from '../../../constants/roleConstants';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsProperEndpoint,
  getDashboardVisitsProperEndpoint,
  getAdvisoryLeadsEndpoint,
  createAdvisoryLeadEndpoint,
  CreateAdvisoryLeadPayload,
} from '../services/dashboardService';
import useAdminEntity from '../../../hooks/useAdminEntity';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  PeriodMetrics,
  ProperPreSaleMetrics,
  PropersAssignedPreSaleVisits,
  PropersAssignedVisits,
  ProperV2Metrics,
  AdvisoryLead,
} from '../models/dashboardModels';
import { getAdvisoryErrorMessage } from '../adapters/advisoryAdapters';
import { Alert, Button, Col, Container, Row } from 'reactstrap';
import { IconPlus } from '@tabler/icons-react';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitManagerListTable from './DashboardVisitManagerListTable';
import DashboardPreSaleVisitManagerListTable from './DashboardPreSaleVisitManagerListTable';
import DashboardAdvisoryListTable from './DashboardAdvisoryListTable';
import AdvisoryLeadDetailModal from './AdvisoryLeadDetailModal';
import CreateAdvisoryLeadModal from './CreateAdvisoryLeadModal';
import AdvisoryMetricsDashboard from './AdvisoryMetricsDashboard';
import { activeTab } from '../types/dashboard.types';
import NavigationTabDashboard from './NavigationTabDashboard';
import DashboardFilterBar from './DashboardFilterBar';
import MetricsDashboard from './MetricsDashboard';
import MetricsDateDashboard from './MetricsDateDashboard';
import useUserCountry from '../../../hooks/useUserCountry';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';

const noOpMetricsCall = (period: PeriodMetrics) => ({
  call: () =>
    Promise.resolve({
      status: 200,
      data: { data: { period, visits: [] } },
    }),
  controller: new AbortController(),
});

const noOpListCall = () => ({
  call: () =>
    Promise.resolve({
      status: 200,
      data: { data: { items: [], meta: { page: 1, pageCount: 0 } } },
    }),
  controller: new AbortController(),
});

const InternalUserDashboard: React.FC = () => {
  const { getAuthToken, getUserRoles } = useAuth();
  const token = getAuthToken();
  const { getCurrentCountry } = useUserCountry();
  const currenUserCountryCode = getCurrentCountry()?.code || '';

  const SDR_ROLE_TAB_MAP: Record<string, activeTab> = {
    [ADMIN_ROLES.SDR_RENT]: 'rent',
    [ADMIN_ROLES.SDR_SALES]: 'secondary',
    [ADMIN_ROLES.SDR_PRESALES]: 'pre-sale',
  };

  const userRoles = getUserRoles();
  const matchedSdrRoles = userRoles.filter(
    (role: string) => role in SDR_ROLE_TAB_MAP,
  );
  const restrictedTab: activeTab | null =
    userRoles.length === 1 && matchedSdrRoles.length === 1
      ? SDR_ROLE_TAB_MAP[matchedSdrRoles[0]]
      : null;

  // Deep-link al detalle de una asesoría: ?tab=advisory&leadId=<id> (usado
  // por el link "Ver en Admin" de la notificación de Slack) — al montar, si
  // la URL ya trae leadId se abre el modal directo, sin esperar a que cargue
  // la lista (el modal resuelve el lead por su cuenta).
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailLeadId, setDetailLeadId] = useState<string | null>(
    searchParams.get('leadId'),
  );

  const VALID_TABS: activeTab[] = ['pre-sale', 'rent', 'secondary', 'advisory'];
  const tabFromUrl = searchParams.get('tab') as activeTab | null;

  // La tab activa persiste en `?tab=` para que un F5 (o compartir el link)
  // deje al usuario donde estaba, en vez de resetear siempre al default.
  const [activeTab, setActiveTab] = useState<activeTab>(
    restrictedTab ??
      (tabFromUrl && VALID_TABS.includes(tabFromUrl)
        ? tabFromUrl
        : currenUserCountryCode === 'SV'
          ? 'rent'
          : 'pre-sale'),
  );

  const openLeadDetail = (leadId: string) => {
    setDetailLeadId(leadId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'advisory');
      next.set('leadId', leadId);
      return next;
    });
  };

  const closeLeadDetail = () => {
    setDetailLeadId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('leadId');
      return next;
    });
  };

  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [selectedSdrId, setSelectedSdrId] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');

  const handleDateRangeChange = (newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleTabChange = (tab: activeTab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
    setShowOnlyAssigned(false);
    setStartDate('');
    setEndDate('');
    setStatus('');
    setSelectedSdrId('');
    setDeliveryTime('');
  };

  const handleClearFilters = () => {
    setShowOnlyAssigned(false);
    setStartDate('');
    setEndDate('');
    setStatus('');
    setSelectedSdrId('');
    setDeliveryTime('');
  };

  const defaultPeriod: PeriodMetrics = { end: '', start: '' };

  // Rent metrics
  const getRentMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'rent') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_rent', {
      onlyAssigned: restrictedTab === 'rent' && showOnlyAssigned,
      startDate: activeTab === 'rent' ? startDate || undefined : undefined,
      endDate: activeTab === 'rent' ? endDate || undefined : undefined,
      status: activeTab === 'rent' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'rent'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [rentMetrics] = useAdminEntity<ProperV2Metrics>(
    getRentMetrics,
    console.error,
  );

  // Secondary metrics
  const getSecondaryMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'secondary') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_sale', {
      onlyAssigned: restrictedTab === 'secondary' && showOnlyAssigned,
      startDate:
        activeTab === 'secondary' ? startDate || undefined : undefined,
      endDate: activeTab === 'secondary' ? endDate || undefined : undefined,
      status: activeTab === 'secondary' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'secondary'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [secondaryMetrics] = useAdminEntity<ProperV2Metrics>(
    getSecondaryMetrics,
    console.error,
  );

  // Pre-sale metrics
  const getDashboardPreSaleMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'pre-sale') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsPreSaleEndpoint(token, 'internal-user', {
      onlyAssigned: restrictedTab === 'pre-sale' && showOnlyAssigned,
      startDate: activeTab === 'pre-sale' ? startDate || undefined : undefined,
      endDate: activeTab === 'pre-sale' ? endDate || undefined : undefined,
      status: activeTab === 'pre-sale' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'pre-sale'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  // Rent visits
  const [rentVisits, setRentVisits] = useState<PropersAssignedVisits[]>([]);
  const getRentVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'rent') {
        return noOpListCall();
      }
      const extraParams: string[] = ['marketplace_type=on_rent'];
      if (restrictedTab === 'rent' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'rent' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'rent' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'rent' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'rent' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      return getDashboardVisitsProperEndpoint(
        `${queryParams.replace(/&$/, '')}&${extraParams.join('&')}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const { paginator: rentPaginator, loadItems: rentLoadItems } =
    useAdminPagination(getRentVisits, setRentVisits, console.error);

  // Secondary visits
  const [secondaryVisits, setSecondaryVisits] = useState<
    PropersAssignedVisits[]
  >([]);
  const getSecondaryVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'secondary') {
        return noOpListCall();
      }
      const extraParams: string[] = ['marketplace_type=on_sale'];
      if (restrictedTab === 'secondary' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'secondary' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'secondary' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'secondary' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'secondary' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      return getDashboardVisitsProperEndpoint(
        `${queryParams.replace(/&$/, '')}&${extraParams.join('&')}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const { paginator: secondaryPaginator, loadItems: secondaryLoadItems } =
    useAdminPagination(getSecondaryVisits, setSecondaryVisits, console.error);

  // Pre-sale visits
  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    PropersAssignedPreSaleVisits[]
  >([]);
  const getPreSaleVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'pre-sale') {
        return noOpListCall();
      }
      const extraParams: string[] = [];
      if (restrictedTab === 'pre-sale' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'pre-sale' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'pre-sale' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'pre-sale' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'pre-sale' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardPreSaleVisitsProperEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const {
    paginator: paginatorPreSaleVisits,
    loadItems: dashboardPreSaleVisitLoadItems,
  } = useAdminPagination(
    getPreSaleVisits,
    setDashboardPreSaleVisits,
    console.error,
  );

  // Advisory leads (user-service) — tab propio, fuente única (sin merge con
  // los otros tabs), visible para todos los admins (no gatea por
  // restrictedTab como los tabs SDR de arriba).
  const [advisoryLeads, setAdvisoryLeads] = useState<AdvisoryLead[]>([]);
  const getAdvisoryLeads = useCallback(
    (queryParams: string, token: string) => {
      const extraParams: string[] = [];
      if (activeTab === 'advisory' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'advisory' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'advisory' && status)
        extraParams.push(`status=${status}`);
      if (activeTab === 'advisory' && deliveryTime)
        extraParams.push(`delivery_time=${deliveryTime}`);
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getAdvisoryLeadsEndpoint(`${queryParams}${separator}`, token);
    },
    [activeTab, startDate, endDate, status, deliveryTime],
  );
  const { paginator: advisoryPaginator, loadItems: advisoryLoadItems } =
    useAdminPagination(getAdvisoryLeads, setAdvisoryLeads, console.error);

  const { callEndpoint } = useCallApiAndLoad();
  const [isCreateAdvisoryOpen, setIsCreateAdvisoryOpen] = useState(false);
  const [advisoryToast, setAdvisoryToast] = useState<{
    color: 'success' | 'danger';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!advisoryToast) return;
    const timer = setTimeout(() => setAdvisoryToast(null), 5000);
    return () => clearTimeout(timer);
  }, [advisoryToast]);

  const handleCreateAdvisoryConfirm = async (
    payload: CreateAdvisoryLeadPayload,
  ): Promise<string | null> => {
    const { status, data } = await callEndpoint(
      createAdvisoryLeadEndpoint(payload, token),
    );
    if (status !== 200 && status !== 201) {
      const errorMessage = getAdvisoryErrorMessage(
        data,
        'No se pudo crear la asesoría.',
      );
      setAdvisoryToast({ color: 'danger', message: errorMessage });
      return errorMessage;
    }
    advisoryLoadItems(advisoryPaginator.page);
    setAdvisoryToast({ color: 'success', message: 'Asesoría creada correctamente.' });
    return null;
  };

  const handleAdvisoryError = (message: string) => {
    setAdvisoryToast({ color: 'danger', message });
  };

  if (!rentMetrics || !secondaryMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentInfo = toStatsMetrics(rentMetrics.visits, VISIT_STATUS_MAP);
  const secondaryInfo = toStatsMetrics(
    secondaryMetrics.visits,
    VISIT_STATUS_MAP,
  );
  const preSaleInfo = toStatsMetrics(
    dashboardPreSaleMetrics.visits,
    PRE_SALE_STATUS_MAP,
  );

  const activeStats =
    activeTab === 'rent'
      ? rentInfo
      : activeTab === 'secondary'
        ? secondaryInfo
        : preSaleInfo;

  const activePeriod =
    activeTab === 'rent'
      ? (rentMetrics?.period ?? defaultPeriod)
      : activeTab === 'secondary'
        ? (secondaryMetrics?.period ?? defaultPeriod)
        : (dashboardPreSaleMetrics?.period ?? defaultPeriod);

  return (
    <Container className="mt-6" fluid>
      {advisoryToast && (
        // El componente Toast de reactstrap depende del partial `toast` de
        // Bootstrap, que este build (Argon Dashboard) no importa en
        // argon-dashboard-pro-react.scss — se renderiza sin estilos. Alert sí
        // está importado ahí, así que se usa como toast fijo en la esquina.
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 2000,
            minWidth: 320,
            maxWidth: 420,
          }}
        >
          <Alert
            color={advisoryToast.color}
            toggle={() => setAdvisoryToast(null)}
            className="shadow"
          >
            <strong>{advisoryToast.color === 'success' ? 'Éxito: ' : 'Error: '}</strong>
            {advisoryToast.message}
          </Alert>
        </div>
      )}

      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={handleTabChange}
        preSaleCount={preSaleInfo.total}
        rentCount={rentInfo.total}
        secondaryCount={secondaryInfo.total}
        advisoryCount={advisoryPaginator.itemCount}
        // Advisory es visible para todos los admins, incluso cuenta
        // restringida a un solo tab SDR (ver decisión del plan).
        visibleTabs={restrictedTab ? [restrictedTab, 'advisory'] : undefined}
      />

      <DashboardFilterBar
        activeTab={activeTab}
        isRestricted={!!restrictedTab}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        status={status}
        onStatusChange={setStatus}
        showOnlyAssigned={showOnlyAssigned}
        onShowOnlyAssignedChange={setShowOnlyAssigned}
        selectedSdrId={selectedSdrId}
        onSdrChange={setSelectedSdrId}
        onClearFilters={handleClearFilters}
        hideAssignmentControl={activeTab === 'advisory'}
        deliveryTime={deliveryTime}
        onDeliveryTimeChange={setDeliveryTime}
      />

      {activeTab !== 'advisory' && (
        <>
          <MetricsDateDashboard activeTab={activeTab} period={activePeriod} />
          <MetricsDashboard activeTab={activeTab} stats={activeStats} />
        </>
      )}

      {activeTab === 'advisory' && (
        <AdvisoryMetricsDashboard
          leads={advisoryLeads}
          totalCount={advisoryPaginator.itemCount}
        />
      )}

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12}>
            <DashboardVisitManagerListTable
              visits={rentVisits}
              footer={
                <AdminPaginator
                  page={rentPaginator.page}
                  totalPages={rentPaginator.pageCount}
                  onPageChange={rentLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      {activeTab === 'secondary' && (
        <Row>
          <Col sm={12}>
            <DashboardVisitManagerListTable
              visits={secondaryVisits}
              showIntentBanner
              footer={
                <AdminPaginator
                  page={secondaryPaginator.page}
                  totalPages={secondaryPaginator.pageCount}
                  onPageChange={secondaryLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            <DashboardPreSaleVisitManagerListTable
              visits={dashboardPreSaleVisits}
              footer={
                <AdminPaginator
                  page={paginatorPreSaleVisits.page}
                  totalPages={paginatorPreSaleVisits.pageCount}
                  onPageChange={dashboardPreSaleVisitLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      {activeTab === 'advisory' && (
        <Row>
          <Col sm={12}>
            <DashboardAdvisoryListTable
              leads={advisoryLeads}
              onViewDetail={(lead) => openLeadDetail(lead.id)}
              headerAction={
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => setIsCreateAdvisoryOpen(true)}
                >
                  <IconPlus size={16} className="me-1" />
                  Crear asesoría agnostica
                </Button>
              }
              footer={
                <AdminPaginator
                  page={advisoryPaginator.page}
                  totalPages={advisoryPaginator.pageCount}
                  onPageChange={advisoryLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      <AdvisoryLeadDetailModal
        leadId={detailLeadId}
        onClose={closeLeadDetail}
        onChanged={() => advisoryLoadItems(advisoryPaginator.page)}
        onError={handleAdvisoryError}
      />

      <CreateAdvisoryLeadModal
        isOpen={isCreateAdvisoryOpen}
        toggle={() => setIsCreateAdvisoryOpen(false)}
        onConfirm={handleCreateAdvisoryConfirm}
      />
    </Container>
  );
};

export default InternalUserDashboard;
